import { SxProps, Theme } from '@mui/material';

/** Shared input styling for filter text fields, selects, and autocompletes. */
export const filterInputSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: 32,
    fontSize: '12.5px',
    borderRadius: '6px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
    '&.Mui-focused fieldset': { borderColor: '#1D9AAA', borderWidth: 1 },
  },
  '& .MuiInputLabel-root': {
    fontSize: '12.5px',
    color: '#5f6d7e',
    '&.Mui-focused': { color: '#1D9AAA' },
  },
};

/** Smaller font variant of filterInputSx for datetime-local inputs. */
export const dateInputSx: SxProps<Theme> = {
  ...filterInputSx,
  '& .MuiOutlinedInput-root': {
    ...(filterInputSx as any)['& .MuiOutlinedInput-root'],
    fontSize: '11.5px',
  },
};

/** Chip style for dropdown-trigger chips (e.g. "More Filters", sort chips). */
export const dropdownChipSx: SxProps<Theme> = {
  height: 32,
  borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.12)',
  backgroundColor: '#fff',
  fontSize: '12.5px',
  color: '#1a2332',
  cursor: 'pointer',
  px: 0.5,
  '&:hover': { borderColor: 'rgba(0,0,0,0.24)', backgroundColor: '#fafafa' },
  '& .MuiChip-label': { px: 1 },
  '& .MuiChip-deleteIcon': { fontSize: 16, color: '#5f6d7e', mr: 0.25 },
};

/** Chip style for active filter pills showing applied filter values. */
export const activeFilterPillSx: SxProps<Theme> = {
  height: 32,
  borderRadius: '6px',
  border: '1px solid #1D9AAA',
  backgroundColor: 'rgba(29,154,170,0.06)',
  fontSize: '12.5px',
  color: '#1a2332',
  cursor: 'default',
  px: 0.5,
  '& .MuiChip-label': { px: 1 },
  '& .MuiChip-deleteIcon': {
    fontSize: 14,
    color: '#5f6d7e',
    mr: 0.25,
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  '&:hover .MuiChip-deleteIcon': { opacity: 1 },
};

/** Subtle outlined button — primary teal variant (used for action buttons in table headers). */
export const btnSubtlePrimarySx = {
  height: 32,
  px: 1.5,
  borderRadius: '6px',
  border: '1px solid #1D9AAA',
  bgcolor: '#fff',
  fontSize: '12.5px',
  fontWeight: 500,
  color: '#1D9AAA',
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  '&:hover': { borderColor: '#178a99', bgcolor: 'rgba(29,154,170,0.04)' },
  '&.Mui-disabled': { borderColor: '#BDC3D2', color: '#B0B0B0', bgcolor: '#fff' },
  '& .MuiButton-startIcon': { mr: 0.5 },
};

/** Subtle outlined button — neutral gray variant (becomes teal on hover). */
export const btnSubtleNeutralSx = {
  height: 34,
  px: '14px',
  borderRadius: '7px',
  border: '1px solid #BDC3D2',
  bgcolor: '#fff',
  fontSize: '12.5px',
  fontWeight: 500,
  color: '#5f6d7e',
  textTransform: 'none' as const,
  gap: '6px',
  '&:hover': {
    borderColor: '#1D9AAA',
    color: '#1D9AAA',
    bgcolor: 'rgba(29,154,170,0.04)',
  },
  '& .MuiButton-startIcon': { mr: 0 },
};

/** Chip-based autocomplete input styling for multi-value filters in the main filter bar. */
export const chipAutocompleteInputSx: SxProps<Theme> = {
  width: 180,
  '& .MuiOutlinedInput-root': {
    height: 32,
    fontSize: '12.5px',
    borderRadius: '6px',
    backgroundColor: '#fff',
    py: '0 !important',
    pl: '8px !important',
    pr: '32px !important',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
    '&.Mui-focused fieldset': { borderColor: '#1D9AAA', borderWidth: 1 },
  },
  '& .MuiAutocomplete-tag': { m: '2px', maxWidth: '60px', height: 20 },
  '& .MuiAutocomplete-endAdornment': {
    position: 'absolute',
    right: '6px !important',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-clearIndicator': {
    fontSize: '18px',
    padding: '2px',
  },
  '& .MuiAutocomplete-input': { minWidth: '40px !important', fontSize: '12.5px', p: '2px 4px !important' },
  '& .MuiInputLabel-root': {
    fontSize: '12.5px',
    color: '#5f6d7e',
    '&.Mui-focused': { color: '#1D9AAA' },
  },
};
